import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  inject,
  OnInit,
} from '@angular/core';
import { GiftCardApiService } from '@features/gift-card/services/gift-card-api/gift-card-api.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { CmsFacade } from '@rd/cms/facades/cms.facade';
import { WindowRefService } from '@rd/core/services/window-ref/window-ref.service';
import { ENV, TENANT_NAME } from '@rd/core/tokens/rd-lib.token';
import { GiftCard } from '@rd/types';
import { CampaignBanner, CmsPageContentType } from '@rd/types/cms.type';
import { isNotNull } from '@rd/utils/isNotNull';
import { getUrlParamId } from '@rd/wl/anz/utils/anz-page-discovery.utils';
import { AnzEnvironmentType } from 'global-types';
import { Observable } from 'rxjs';
import { filter } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'anz-campaign-banner-gc-details',
  templateUrl: './campaign-banner-gc-details.component.html',
  styleUrls: ['./campaign-banner-gc-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AnzCampaignBannerGCDetailsComponent implements OnInit {
  private cmsFacade = inject(CmsFacade);

  campaignBanner: CampaignBanner | null = null;
  showBanner: boolean;

  constructor(
    private cdr: ChangeDetectorRef,
    private windowRefService: WindowRefService,
    private giftCardApiService: GiftCardApiService,
    @Inject(TENANT_NAME) private tenantName: string,
    @Inject(ENV) private env: AnzEnvironmentType,
  ) {}

  ngOnInit(): void {
    this.getGiftCardUsingId()
      .pipe(untilDestroyed(this))
      .subscribe((giftCard: GiftCard) => {
        if (giftCard) {
          this.getCampaignBanner(giftCard.name);
        }
      });
  }

  private getCampaignBanner(giftCardName: string): void {
    const bannerId = `${this.tenantName}--${giftCardName
      .toLowerCase()
      .split(' ')
      .join('')}-campaign-banner-gc-details`;

    this.cmsFacade
      .getContentByName({
        pageName: bannerId,
        pageType: 'campaignbanner',
        locale: 'en-au',
      })
      .pipe(untilDestroyed(this), filter(isNotNull))
      .subscribe((campaignBanner: CmsPageContentType) => {
        if (campaignBanner.type === 'campaignbanner') {
          this.campaignBanner = campaignBanner;
          // We should rename showBanner flag to showBannerInProduction
          // We will do this later as this affect the campaigns in production
          this.showBanner =
            this.env === 'production' ? this.campaignBanner?.showBanner : true;
          this.cdr.markForCheck();
        }
      });
  }

  private getGiftCardUsingId(): Observable<GiftCard> {
    return this.giftCardApiService.getGiftCard(
      getUrlParamId(this.windowRefService.nativeWindow.location.href),
    );
  }
}
